import { useEffect, useRef } from "react";

// Task #1676: one-shot reverse-geocode prefill hook used by the "Request a
// change" modal and the conversation reservation panel. Lives in @/hooks (not
// the page module) so a shared component can import it without creating a
// component → page chunk cycle. Imports nothing beyond React + fetch, so it
// never drags a vendor (Twilio) dependency into the boot graph.

export interface ReverseGeocodePrefillOptions {
  enabled: boolean;
  isDelivery: boolean | null | undefined;
  airportCode: string | null | undefined;
  address: string | null | undefined;
  lat: number | null | undefined;
  lng: number | null | undefined;
  onResolved: (displayName: string) => void;
  onReset?: () => void;
  apiBase?: string;
}

// When `enabled` flips to true for a delivery trip that has coords but no
// address/airport, it resolves the lat/lng once via reverseGeocodeOnce and
// calls onResolved with the result. The cache key (lat,lng) is held by a ref
// so re-renders don't refetch and a different trip can resolve cleanly next
// open. When `enabled` flips back to false, onReset fires.
export function useReverseGeocodePrefill(opts: ReverseGeocodePrefillOptions): void {
  const { enabled, isDelivery, airportCode, address, lat, lng, onResolved, onReset, apiBase } = opts;
  // Cache the (lat,lng) we've already requested via a ref so re-renders
  // triggered by the resolved-state setter don't cancel the in-flight
  // fetch (the previous version stored this in a useState bucket, which
  // re-ran the effect mid-flight and dropped the result).
  const lastKeyRef = useRef<string | null>(null);
  // Hold the latest callbacks in a ref so the effect deps stay narrow
  // (only the inputs that actually gate fetching) without violating
  // the exhaustive-deps lint.
  const onResolvedRef = useRef(onResolved);
  const onResetRef = useRef(onReset);
  onResolvedRef.current = onResolved;
  onResetRef.current = onReset;
  useEffect(() => {
    if (!enabled) {
      lastKeyRef.current = null;
      onResetRef.current?.();
      return;
    }
    if (!isDelivery) return;
    if (airportCode) return;
    if (address) return;
    if (lat == null || lng == null) return;
    const key = `${lat},${lng}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    void reverseGeocodeOnce(lat, lng, apiBase).then((displayName) => {
      if (lastKeyRef.current !== key) return;
      if (!displayName) return;
      onResolvedRef.current(displayName);
    });
  }, [enabled, isDelivery, airportCode, address, lat, lng, apiBase]);
}

// One-shot reverse-geocode helper. Used by the hook above (and re-exported for
// the page-side modules) when a delivery trip has coords but no persisted
// `delivery_address`. Returns the best display name we can resolve, or null.
export async function reverseGeocodeOnce(lat: number, lon: number, base?: string): Promise<string | null> {
  try {
    const apiBase = base ?? `${import.meta.env.BASE_URL || "/"}api`;
    const res = await fetch(`${apiBase}/address/reverse?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}`);
    if (!res.ok) return null;
    const data = await res.json() as { result?: { displayName?: string } | null };
    return data.result?.displayName ?? null;
  } catch {
    return null;
  }
}
